/* SiteMultiEditViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const SiteCFUtility = require("../../../../lib/SiteCfUtility");
const Renderer = require("./SiteMultiEditViewRenderer");
const InheritedFieldUtility = require("../../../../lib/InheritedFieldUtility");

class SiteMultiEditViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        MUF,
        solutionsManager,
        SiteEntity,
        CustomFieldsEntity,
        Credentials
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = MUF;
        this._tabComponent = this._gameEditor.getTabCmpnt();

        this._userSession = userSession;
        this._siteEntityDef = SiteEntity;
        this._CustomFieldsEntityDef = CustomFieldsEntity;
        this._CredentialsEntity = Credentials;

        this._solutionDefinition = solutionsManager.getActiveSolution();
        this._solutionName = this._solutionDefinition.name || "";
        this._sites = undefined;
        this._customFieldColumnDef = undefined;
        this._inheritedCustomFields = [];
        this._siteCfUtility = new SiteCFUtility();
        this._InheritedFieldUtility = InheritedFieldUtility;

        this._gameEditor.hideCabinetCpmnt();
        this._renderer = new Renderer(this);

        this._registerListeners();
    }

    get customFieldsJtypes() {
        return Object.entries(this._customFieldColumnDef)
            .filter(
                ([, customFieldDef]) =>
                    _.get(customFieldDef, "enabled") &&
                    !_.get(customFieldDef, "systemOwned") &&
                    !_.get(customFieldDef, "deviceOnly", true)
            )
            .map(([customFieldId, customFieldDef]) =>
                Object.assign({id: customFieldId}, customFieldDef)
            )
            .sort(this._CustomFieldsEntityDef.getSortingComparator("order"))
            .map(this.gameEditor.generateCustomFieldJtype);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._tabComponent.on(
            "DirtyChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
    }

    _unregisterListeners() {
        this._tabComponent.on(
            "DirtyChanged",
            (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
        );
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    async shutdown() {
        this._unregisterListeners();

        return this._renderer.shutdown().then(
            () => {
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
                this._siteEntityDef = undefined;
                this._sites = undefined;
                this._inheritedCustomFields = undefined;
            },
            (err) => {
                this._logger.warn(err, "SiteDetailsViewComponent.shutdown() error.");
            }
        );
    }

    getCreds(orgId, query = {}) {
        query = Object.assign(query, {
            $and: [{hidden: {$ne: true}}, {credsType: {$eq: "deviceUserPass"}}],
        });
        return this._CredentialsEntity.loadCredentials(orgId, query);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onDirtyStateChanged(dirtyState) {
        this.emit("Dirty", dirtyState);

        let onBeforeUnloadFunc = () => {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text",
                    },
                })
                .then(() => {
                    return this.saveSite().then(_.noop); // it will not close the tab if it will have something in result
                })
                .catch((err) => {
                    if (err.userRejected) {
                        return this._modalUserFeedback.hideDisplayed().then(_.noop);
                    } else {
                        console.error("Error in onBeforeUnloadFunc! Err:", err);
                    }
                });
        };

        if (this.isDirty()) {
            this._gameEditor.setOnBeforeUnload(onBeforeUnloadFunc);
        } else {
            this._gameEditor.clearOnBeforeUnload();
        }
    }

    _onPendingPatchesChanged() {
        // TODO - this is a quick hack because the Array ref adapter does a shutdown and complete rebuild every time you call setValue. the effect to the user is they lose focus in the proxy list if they stop typing longer than the debounce
        // this.emit('SitePropertiesChanged');
    }

    _onPristinePropertiesChanged(pristineProperties, before) {
        this.emit("SitePropertiesChanged", pristineProperties, before);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _getCredOptions(credEtys) {
        let opts = {};
        opts["_Site"] = "_Site";

        credEtys.forEach((cred) => {
            opts[cred.name] = cred.id;
        });
        return opts;
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    get editorMode() {
        return this.getEditorMode();
    }

    getEditorMode() {
        return this._gameEditor.getInstanceEditorStateProperty("details.editorMode") || "general";
    }

    set editorMode(editorState) {
        return this.setEditorMode(editorState);
    }

    setEditorMode(editorState, silent) {
        let old = this.getEditorMode();
        this._gameEditor.setInstanceEditorStateProperty("details.editorMode", editorState);
        if (!silent) {
            this.emit("EditorModeChanged", editorState, old);
        }
        return this;
    }

    isDirty() {
        return this._tabComponent.getDirty();
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    setTabDirty(state) {
        this._tabComponent.setDirty(state);
    }

    closeTab() {
        return this._gameEditor.closeEditor();
    }

    whenModelIsReady() {
        return Promise.all([
            this._CustomFieldsEntityDef.loadColumnDefinitions(
                this._userSession.activeOrganization.id
            ),
            this._gameEditor.whenSiteIsReady(),
            this._CustomFieldsEntityDef.loadCustomFields(this._userSession.activeOrganization.id),
        ])
            .then(([customFieldColumnDef, sitesEntities, customFieldEty]) => {
                this._sites = this.extractSitesData(sitesEntities);
                this._customFieldsList = customFieldEty.fields;
                this._customFieldColumnDef = customFieldColumnDef;
                this._inheritableCfs = Object.keys(this._customFieldColumnDef).filter(
                    (cf) => !this._customFieldColumnDef[cf].deviceOnly
                );
                return Promise.resolve();
            })
            .then(() => {
                return this.getCreds(this._userSession.activeOrganization.id);
            })
            .then((credEty) => {
                this._credEtys = credEty;
                this.credOptions = this._getCredOptions(credEty);
            })
            .catch((err) => {
                if (err !== "sites no longer exist") {
                    this._modalUserFeedback.displayError({
                        errorMsg: "This sites cannot be opened.",
                        errorCode: "",
                    });
                    this._logger.warn(err, "SiteMultiEditComponent.whenModelIsReady() error");
                }
                return Promise.reject(err);
            });
    }

    extractSitesData(sitesEntities = []) {
        return sitesEntities.reduce(
            (siteProperties, site) =>
                siteProperties.concat(_.set(site.getPristineProperties(), "id", site.id)),
            []
        );
    }

    isSiteFormValid(dataToSave) {
        const dropdowns = ["locationType"],
            inputChanges = dataToSave,
            isInputLatNaN = _.has(inputChanges, "latitude")
                ? this._latLonCheck(false, inputChanges.latitude)
                : true,
            isInputLonNaN = _.has(inputChanges, "longitude")
                ? this._latLonCheck(false, inputChanges.longitude)
                : true;

        dropdowns.forEach((field) => {
            if (_.has(inputChanges, field) && _.isEmpty(inputChanges[field])) {
                throw this._errorObj(field);
            }
        });

        if (!isInputLatNaN) {
            throw this._errorObj("latitude");
        }

        if (!isInputLonNaN) {
            throw this._errorObj("longitude");
        }

        return true;
    }

    _latLonCheck(isRequired, input) {
        if (!isRequired) {
            if (input.trim().length === 0) {
                return true;
            }
            return !isNaN(input);
        }

        if (input.trim().length === 0) {
            return false;
        }

        return !isNaN(input);
    }

    _errorObj(field) {
        return {
            errorMsg: `Data is invalid. Please enter a valid ${field} value.`,
            errorCode: "SyntaxError",
            title: "Error",
        };
    }

    updateSites() {
        const ids = this._sites.map((site) => site.id);
        return this._gameEditor._initMultipleSites(ids).then((sitesEntities) => {
            this._sites = this.extractSitesData(sitesEntities);
            return this._sites;
        });
    }

    saveSite(formData) {
        const tenantId = this._gameEditor._activeOrgId;
        let operations = [],
            dataToSave = _.isUndefined(formData) ? this._renderer._getFormValue() : formData;

        if (!this.isSiteFormValid(dataToSave)) {
            return Promise.resolve();
        }
        this._inheritedCustomFields = this._siteCfUtility.getInheritedCfs();
        this._modalUserFeedback.displayLoading({
            message: "Updating sites... This could take a moment",
        });

        const maxSize = 500;

        const selectedUpdates = new Array(Math.floor(operations.length / maxSize));
        let sum = 0,
            lastSum = 0;

        const showProgFunc = (prog) => {
            let totalCount = operations.length;
            let updatedSum = 0;

            if (prog.progressCount >= sum && lastSum < maxSize) {
                updatedSum = sum = prog.progressCount;
            } else {
                updatedSum = sum + prog.progressCount;
            }

            if (prog.progressCount === maxSize && lastSum > maxSize) {
                sum = sum + prog.progressCount;
            }

            lastSum = updatedSum;

            this._modalUserFeedback.showLoadingProgress(
                updatedSum + " of " + totalCount + " site" + (totalCount > 1 ? "s" : "")
            );
        };

        for (let key in dataToSave) {
            if (key === "customFieldValues") {
                const customFieldValues = dataToSave.customFieldValues;
                Object.keys(customFieldValues).forEach((cfid) => {
                    dataToSave["customFieldValues." + cfid] = customFieldValues[cfid];
                    dataToSave["customFieldSources." + cfid] = null;
                });
                this._inheritedCustomFields.forEach((cf) => {
                    dataToSave["customFieldSources." + cf] = {};
                });
                delete dataToSave.customFieldValues;
                this._siteCfUtility.clearInheritedCfs();
            }

            if (key === "discoveryLevel") {
                if (
                    _.get(this._renderer, "_discoveryLevelInheritanceUtility._isInherited", false)
                ) {
                    dataToSave["discoveryLevelFrom"] = "_Site";
                    dataToSave["discoveryLevel"] = null;
                } else {
                    dataToSave["discoveryLevelFrom"] = null;
                }
            }
            if (key === "credsName") {
                dataToSave["credsId"] = "_from_name_";
                dataToSave["credsFrom"] = null;
            }
        }

        this._sites.forEach((site) => {
            operations.push({
                actionType: "entity",
                action: "update",
                entityType: "Site",
                entityId: site.id,
                properties: dataToSave,
            });
        });

        operations.forEach((selection, idx) => {
            const i = Math.floor(idx / maxSize);
            if (!selectedUpdates[i]) {
                selectedUpdates[i] = [];
            }
            selectedUpdates[i].push(selection);
        });

        const sleep = () => {
            return new Promise((resolve) => {
                return setTimeout(resolve, 3000);
            });
        };

        const promises = [];
        selectedUpdates.forEach((currentBatch) => {
            promises.push(() =>
                this._gameEditor._siteEntity.doBulk(tenantId, currentBatch, showProgFunc, 100)
            );
            promises.push(sleep);
        });

        return promises
            .reduce((accumulatorPromise, nextPromise) => {
                return accumulatorPromise.then(() => {
                    return nextPromise();
                });
            }, Promise.resolve())
            .then((/*result*/) => {
                this.setTabDirty(false);
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn({error: err}, "SiteDetailsViewComponent.saveSite() error.");

                if (_.get(err, "errorCode") === "EntityInteractionsError") {
                    this.setTabDirty(false);
                }
                return err;
            })
            .then((error) => {
                return this.updateSites().then((sites) => {
                    if (!sites.length) {
                        error = {
                            errorMsg:
                                "The selected sites have all been deleted by another user before changes could be saved. This form is now going to be closed.",
                            errorCode: "EntityInteractionsError",
                            title: "Error",
                        };
                    }
                    return error && Promise.reject(error);
                });
            });
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = SiteMultiEditViewComponent;

SiteMultiEditViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.solutionsManager",
    "$teve.entities.Site",
    "$teve.entities.CustomFields",
    "$teve.entities.Credentials",
];

SiteMultiEditViewComponent.$displayDetails = {
    type: "View",
    name: "Site Multi-Edit",
    icon: "common.edit",
    shown: false,
    title: "MultiEdit",
    order: 1,
};
